import {
  BellRing,
  CheckCircle2,
  ChevronRight,
  CircleDollarSign,
  Clock3,
  FolderOpen,
  Gauge,
  LayoutDashboard,
  MessageCircleMore,
  MonitorUp,
  ShieldCheck,
  Sparkles,
  SunMedium,
  UsersRound,
  Zap,
} from "lucide-react";
import Reveal from "./Reveal";
import "../unified-workspace-section.css";

const oldTools = [
  { id: "message", label: "Messaging", Icon: MessageCircleMore },
  { id: "tasks", label: "Tasks", Icon: CheckCircle2 },
  { id: "files", label: "File Sharing", Icon: FolderOpen },
  { id: "remote", label: "Remote Support", Icon: MonitorUp },
  { id: "alerts", label: "Notifications", Icon: BellRing },
];

const orbitTools = [
  { id: "message", label: "Messaging", Icon: MessageCircleMore },
  { id: "tasks", label: "Tasks", Icon: CheckCircle2 },
  { id: "remote", label: "Remote\nSupport", Icon: MonitorUp },
  { id: "team", label: "Team\nCollaboration", Icon: UsersRound },
  { id: "files", label: "File Sharing", Icon: FolderOpen },
  { id: "secure", label: "Secure Access", Icon: ShieldCheck },
];

const platformBenefits = [
  {
    id: "platform",
    title: "One Platform.",
    copy: "Everything connected.",
    Icon: Zap,
  },
  {
    id: "secure",
    title: "Secure & Reliable.",
    copy: "Enterprise-grade security you can trust.",
    Icon: ShieldCheck,
  },
  {
    id: "visibility",
    title: "Workflow Visibility.",
    copy: "See everything in real time and make smarter decisions.",
    Icon: Gauge,
  },
  {
    id: "together",
    title: "Stronger Together.",
    copy: "Teams, tools, and data—aligned for success.",
    Icon: UsersRound,
  },
];

const outcomes = [
  {
    id: "time",
    title: "Save Time",
    copy: "Eliminate app switching and streamline your day.",
    Icon: Clock3,
  },
  {
    id: "cost",
    title: "Reduce Costs",
    copy: "Replace multiple subscriptions with one connected platform.",
    Icon: CircleDollarSign,
  },
  {
    id: "productivity",
    title: "Boost Productivity",
    copy: "Everything you need, right where you work.",
    Icon: LayoutDashboard,
  },
  {
    id: "smarter",
    title: "Work Smarter",
    copy: "Automate, collaborate, and achieve more—together.",
    Icon: SunMedium,
  },
];

function FlowGate({ side }) {
  return (
    <div className={`uws-flow-gate uws-flow-gate--${side}`} aria-hidden="true">
      <span className="uws-flow-gate-beam uws-flow-gate-beam--one" />
      <span className="uws-flow-gate-beam uws-flow-gate-beam--two" />
      <span className="uws-flow-gate-beam uws-flow-gate-beam--three" />
      <span className="uws-flow-gate-core">
        <ChevronRight size={28} strokeWidth={2.4} />
      </span>
    </div>
  );
}

function OldWayCloud() {
  return (
    <div className="uws-old-cloud">
      <svg className="uws-chaos-web" viewBox="0 0 420 270" aria-hidden="true">
        <path d="M48 82 C88 45 120 46 155 74 C185 96 214 66 248 58 C292 47 343 73 352 111" />
        <path d="M36 171 C84 128 126 131 158 154 C187 174 211 155 245 140 C290 121 330 132 379 173" />
        <path d="M66 223 C112 195 141 188 177 205 C211 222 245 215 277 191 C306 171 338 180 367 211" />
        <path d="M109 55 C101 102 111 132 140 159 C168 184 173 213 160 243" />
        <path d="M235 39 C224 83 234 111 268 134 C301 156 313 184 301 235" />
        <path d="M44 120 C103 113 150 111 208 122 C265 133 311 123 371 102" />
      </svg>

      <span className="uws-chaos-person uws-chaos-person--one">●</span>
      <span className="uws-chaos-person uws-chaos-person--two">●</span>
      <span className="uws-chaos-person uws-chaos-person--three">●</span>
      <span className="uws-chaos-person uws-chaos-person--four">●</span>
      <span className="uws-chaos-person uws-chaos-person--five">●</span>

      {oldTools.map(({ id, label, Icon }, index) => (
        <div className={`uws-old-tool uws-old-tool--${id}`} key={id} style={{ "--uws-index": index }}>
          <span className="uws-old-tool-icon"><Icon size={17} strokeWidth={2.15} /></span>
          <strong>{label}</strong>
          {id !== "tasks" && <i className="uws-error-badge">!</i>}
        </div>
      ))}
    </div>
  );
}

function CullathOrbit() {
  return (
    <div className="uws-orbit-stage" aria-label="Cullath connected workspace">
      <span className="uws-orbit-aura uws-orbit-aura--one" aria-hidden="true" />
      <span className="uws-orbit-aura uws-orbit-aura--two" aria-hidden="true" />
      <span className="uws-orbit-aura uws-orbit-aura--three" aria-hidden="true" />
      <span className="uws-orbit-ring uws-orbit-ring--outer" aria-hidden="true" />
      <span className="uws-orbit-ring uws-orbit-ring--mid" aria-hidden="true" />
      <span className="uws-orbit-ring uws-orbit-ring--inner" aria-hidden="true" />
      <span className="uws-orbit-particles" aria-hidden="true" />

      {orbitTools.map(({ id, label, Icon }, index) => (
        <div className={`uws-orbit-tool uws-orbit-tool--${id}`} key={id} style={{ "--uws-index": index }}>
          <span className={`uws-orbit-tool-icon uws-orbit-tool-icon--${id}`}>
            <Icon size={19} strokeWidth={2.2} />
          </span>
          <strong>{label.split("\n").map((line) => <span key={line}>{line}</span>)}</strong>
        </div>
      ))}

      <div className="uws-core">
        <span className="uws-core-halo" aria-hidden="true" />
        <span className="uws-core-ring" aria-hidden="true" />
        <div className="uws-core-disc">
          <img src="/2.png" alt="Cullath" />
        </div>
      </div>

      <div className="uws-orbit-caption">Unified. Intelligent. Effortless.</div>
    </div>
  );
}

export default function UnifiedWorkspaceSection() {
  return (
    <section className="uws-section" aria-labelledby="uws-title">
      <div className="uws-grid-dots uws-grid-dots--left" aria-hidden="true" />
      <div className="uws-grid-dots uws-grid-dots--right" aria-hidden="true" />
      <div className="uws-center-glow" aria-hidden="true" />

      <div className="uws-container">
        <Reveal className="uws-heading">
          <span className="uws-kicker"><Sparkles size={14} /> ONE CONNECTED WORKSPACE</span>
          <h2 id="uws-title">
            Replace Multiple Tools
            <span>with One Connected Workspace.</span>
          </h2>
          <p>
            Cullath brings your essential tools, data, and teams into a single intelligent platform—
            so you can focus on what matters most.
          </p>
        </Reveal>

        <div className="uws-story-grid">
          <Reveal className="uws-story-side uws-story-side--old" delay={80}>
            <div className="uws-side-label uws-side-label--old">THE OLD WAY</div>
            <p>Disconnected. Fragmented. Inefficient.</p>
            <OldWayCloud />
          </Reveal>

          <FlowGate side="left" />

          <Reveal className="uws-story-center" delay={170}>
            <CullathOrbit />
          </Reveal>

          <FlowGate side="right" />

          <Reveal className="uws-story-side uws-story-side--new" delay={250}>
            <div className="uws-side-label uws-side-label--new">THE CULLATH WAY</div>
            <p>Connected. Unified. Effortless.</p>
            <div className="uws-benefit-stack">
              {platformBenefits.map(({ id, title, copy, Icon }, index) => (
                <article className={`uws-benefit-card uws-benefit-card--${id}`} key={id} style={{ "--uws-index": index }}>
                  <span className="uws-benefit-icon"><Icon size={24} strokeWidth={2.15} /></span>
                  <div>
                    <h3>{title}</h3>
                    <p>{copy}</p>
                  </div>
                  <ChevronRight className="uws-benefit-arrow" size={18} />
                </article>
              ))}
            </div>
          </Reveal>
        </div>

        <div className="uws-outcomes">
          {outcomes.map(({ id, title, copy, Icon }, index) => (
            <Reveal className={`uws-outcome-reveal uws-outcome-reveal--${id}`} key={id} delay={320 + index * 85}>
              <article className={`uws-outcome-card uws-outcome-card--${id}`}>
                <span className="uws-outcome-icon"><Icon size={34} strokeWidth={2.05} /></span>
                <div className="uws-outcome-copy">
                  <h3>{title}</h3>
                  <p>{copy}</p>
                  <span className="uws-outcome-progress"><i /></span>
                </div>
              </article>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}
